import { useEffect, useRef } from "react";

/**
 * Tiny scroll-progress bar pinned to the top of the viewport.
 * Uses rAF-throttled scroll handler and writes only a CSS transform —
 * no React re-renders, no layout thrash.
 */
export function ScrollProgress() {
  const barRef = useRef<HTMLDivElement | null>(null);
  const ticking = useRef(false);

  useEffect(() => {
    const update = () => {
      ticking.current = false;
      const bar = barRef.current;
      if (!bar) return;
      const doc = document.documentElement;
      const max = (doc.scrollHeight - doc.clientHeight) || 1;
      const p = Math.min(1, Math.max(0, window.scrollY / max));
      bar.style.transform = `scaleX(${p})`;
    };
    const onScroll = () => {
      if (ticking.current) return;
      ticking.current = true;
      requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-x-0 top-0 z-[60] h-[2px] bg-transparent"
    >
      <div
        ref={barRef}
        className="h-full origin-left bg-mint"
        style={{ transform: "scaleX(0)", willChange: "transform" }}
      />
    </div>
  );
}
